function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { ResizeNotifier } from '@theme/utilities';
import { DeclarativeShadowElement } from '@theme/component';

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', {
      bubbles: true
    });
    this.detail = {
      minimumReached
    };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section and other areas.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
var _waitForStyles = _classPrivateFieldLooseKey("waitForStyles");
var _initialize = _classPrivateFieldLooseKey("initialize");
var _scheduled = _classPrivateFieldLooseKey("scheduled");
var _handleIntersection = _classPrivateFieldLooseKey("handleIntersection");
var _handleChange = _classPrivateFieldLooseKey("handleChange");
var _moveItemsToDefaultSlot = _classPrivateFieldLooseKey("moveItemsToDefaultSlot");
var _reset = _classPrivateFieldLooseKey("reset");
var _updateMinimumReached = _classPrivateFieldLooseKey("updateMinimumReached");
var _reflowItems = _classPrivateFieldLooseKey("reflowItems");
var _observeChanges = _classPrivateFieldLooseKey("observeChanges");
var _unobserveChanges = _classPrivateFieldLooseKey("unobserveChanges");
var _refs = _classPrivateFieldLooseKey("refs");
var _resizeObserver = _classPrivateFieldLooseKey("resizeObserver");
var _mutationObserver = _classPrivateFieldLooseKey("mutationObserver");
var _intersectionObserver = _classPrivateFieldLooseKey("intersectionObserver");
export class OverflowList extends DeclarativeShadowElement {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _unobserveChanges, {
      value: _unobserveChanges2
    });
    Object.defineProperty(this, _observeChanges, {
      value: _observeChanges2
    });
    /**
     * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
     * @param {Element[]} visibleElements - The currently visible elements
     */
    Object.defineProperty(this, _updateMinimumReached, {
      value: _updateMinimumReached2
    });
    /**
     * Reset the list to its initial state and disconnect the observers.
     */
    Object.defineProperty(this, _reset, {
      value: _reset2
    });
    /**
     * Move all items to the default slot.
     */
    Object.defineProperty(this, _moveItemsToDefaultSlot, {
      value: _moveItemsToDefaultSlot2
    });
    /**
     * Initialize the element
     */
    Object.defineProperty(this, _initialize, {
      value: _initialize2
    });
    Object.defineProperty(this, _waitForStyles, {
      value: _waitForStyles2
    });
    Object.defineProperty(this, _scheduled, {
      writable: true,
      value: false
    });
    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    Object.defineProperty(this, _handleIntersection, {
      writable: true,
      value: entries => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].disconnect();
          setTimeout(() => {
            // Remove the leftover <template> for dynamically injected <overflow-list> elements.
            this.querySelector(':scope > template[shadowrootmode="open"]')?.remove();
            _classPrivateFieldLooseBase(this, _reflowItems)[_reflowItems](entry.boundingClientRect.height);
          }, 0);
        }
      }
    });
    /**
     * @type {ResizeObserverCallback & MutationCallback}
     */
    Object.defineProperty(this, _handleChange, {
      writable: true,
      value: () => {
        if (!this.offsetParent) return;
        if (_classPrivateFieldLooseBase(this, _scheduled)[_scheduled]) return;
        _classPrivateFieldLooseBase(this, _scheduled)[_scheduled] = true;
        requestAnimationFrame(() => setTimeout(() => {
          _classPrivateFieldLooseBase(this, _reflowItems)[_reflowItems]();
          _classPrivateFieldLooseBase(this, _scheduled)[_scheduled] = false;
        }, 0));
      }
    });
    /**
     * Reflow items based on available space within the list.
     * @param {number} [listHeight] Initial height of the list
     * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
     */
    Object.defineProperty(this, _reflowItems, {
      writable: true,
      value: (listHeight = 0, lastVisibleElement = null) => {
        const {
          defaultSlot,
          overflowSlot,
          moreSlot,
          list,
          placeholder
        } = _classPrivateFieldLooseBase(this, _refs)[_refs];
        _classPrivateFieldLooseBase(this, _unobserveChanges)[_unobserveChanges]();

        // Reset all elements to the default slot so we can check which ones overflow.
        _classPrivateFieldLooseBase(this, _moveItemsToDefaultSlot)[_moveItemsToDefaultSlot]();
        const elements = defaultSlot.assignedElements();
        const lastElement = elements[elements.length - 1];
        if (!lastElement) {
          _classPrivateFieldLooseBase(this, _observeChanges)[_observeChanges]();
          return;
        }

        /** @type {Element[]} */
        let visibleElements = [];
        /** @type {Element[]} */
        let overflowingElements = [];
        let placeholderWidth = 0;
        let hasOverflow = false;
        if (listHeight > 0) {
          list.style.setProperty('height', `${listHeight}px`);
        }

        // Enable flex-wrap so overflowing items break to the next line. This makes calculations easier.
        list.style.setProperty('flex-wrap', 'wrap');
        placeholder.hidden = true;

        // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row.
        moreSlot.style.setProperty('order', '-1');
        moreSlot.hidden = false;
        lastVisibleElement?.style.setProperty('order', '-1');
        const moreSlotRect = moreSlot.getBoundingClientRect();
        elements.forEach(element => {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.top > moreSlotRect.top) {
            if (!overflowingElements.length) {
              placeholderWidth = elementRect.width;
            }
            hasOverflow = true;
            overflowingElements.push(element);
          } else {
            visibleElements.push(element);
          }
        });
        if (hasOverflow) {
          moreSlot.style.removeProperty('order');
        }
        lastVisibleElement?.style.removeProperty('order');

        // Move the elements to the correct slot.
        for (const element of elements) {
          const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
          if (element.slot !== targetSlot) {
            element.slot = targetSlot;
          }
        }
        list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
        this.style.setProperty('--overflow-count', `${overflowingElements.length}`);

        // Adjust the "More" button visibility.
        moreSlot.hidden = !hasOverflow;
        if (hasOverflow) {
          // Set the width and height of the placeholder so the list can grow if there is space.
          placeholder.style.width = `${placeholderWidth}px`;
          placeholder.hidden = false;
        }

        // Reset the overflow property since children elements may need to display outside the list (e.g. dropdowns, popovers).
        list.style.setProperty('overflow', 'unset');
        hasOverflow && _classPrivateFieldLooseBase(this, _updateMinimumReached)[_updateMinimumReached](visibleElements);
        _classPrivateFieldLooseBase(this, _observeChanges)[_observeChanges]();
      }
    });
    /**
     * @type {{
     *   defaultSlot: HTMLSlotElement;
     *   overflowSlot: HTMLSlotElement;
     *   moreSlot: HTMLSlotElement;
     *   overflow: HTMLElement;
     *   list: HTMLUListElement;
     *   placeholder: HTMLLIElement;
     * }}
     */
    Object.defineProperty(this, _refs, {
      writable: true,
      value: void 0
    });
    /**
     * @type {ResizeObserver}
     */
    Object.defineProperty(this, _resizeObserver, {
      writable: true,
      value: new ResizeNotifier(_classPrivateFieldLooseBase(this, _handleChange)[_handleChange])
    });
    /**
     * @type {MutationObserver}
     */
    Object.defineProperty(this, _mutationObserver, {
      writable: true,
      value: new MutationObserver(_classPrivateFieldLooseBase(this, _handleChange)[_handleChange])
    });
    Object.defineProperty(this, _intersectionObserver, {
      writable: true,
      value: new IntersectionObserver(_classPrivateFieldLooseBase(this, _handleIntersection)[_handleIntersection], {
        // Extend the root margin to around one more viewport of a typical mobile screen.
        rootMargin: '640px 360px 640px 360px'
      })
    });
  }
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        _classPrivateFieldLooseBase(this, _reset)[_reset]();
      } else {
        _classPrivateFieldLooseBase(this, _reflowItems)[_reflowItems]();
      }
    }
  }
  async connectedCallback() {
    super.connectedCallback();

    // Styles for dynamically injected <overflow-list> elements are async.
    // We need to wait for them to be loaded before initializing the element to properly calculate the overflow.
    await _classPrivateFieldLooseBase(this, _waitForStyles)[_waitForStyles]();
    _classPrivateFieldLooseBase(this, _initialize)[_initialize]();
    requestAnimationFrame(() => {
      _classPrivateFieldLooseBase(this, _reflowItems)[_reflowItems]();
    });
  }
  disconnectedCallback() {
    _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
    _classPrivateFieldLooseBase(this, _mutationObserver)[_mutationObserver].disconnect();
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].disconnect();
  }
  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function' ? Theme.utilities.scheduler.schedule : /** @param {FrameRequestCallback} callback */callback => requestAnimationFrame(() => setTimeout(callback, 0));
  }
  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }
  get overflowSlot() {
    const {
      overflowSlot
    } = _classPrivateFieldLooseBase(this, _refs)[_refs];
    return overflowSlot;
  }
  get defaultSlot() {
    const {
      defaultSlot
    } = _classPrivateFieldLooseBase(this, _refs)[_refs];
    return defaultSlot;
  }
  /**
   * Show all items in the list.
   */
  showAll() {
    const {
      placeholder
    } = _classPrivateFieldLooseBase(this, _refs)[_refs];
    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }
}
function _waitForStyles2() {
  /** @type {HTMLLinkElement | null | undefined} */
  const styles = this.shadowRoot?.querySelector('link[rel="stylesheet"]');

  // No styles or styles are already loaded.
  if (!styles || styles.sheet) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    styles.addEventListener('load', resolve);
  });
}
function _initialize2() {
  const {
    shadowRoot
  } = this;
  if (!shadowRoot) throw new Error('Missing shadow root');
  const defaultSlot = shadowRoot.querySelector('slot:not([name])');
  const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
  const moreSlot = shadowRoot.querySelector('slot[name="more"]');
  const overflow = shadowRoot.querySelector('[part="overflow"]');
  const list = shadowRoot.querySelector('[part="list"]');
  const placeholder = shadowRoot.querySelector('[part="placeholder"]');
  if (!(defaultSlot instanceof HTMLSlotElement) || !(overflowSlot instanceof HTMLSlotElement) || !(moreSlot instanceof HTMLSlotElement) || !(overflow instanceof HTMLElement) || !(list instanceof HTMLUListElement) || !(placeholder instanceof HTMLLIElement)) {
    throw new Error('Invalid element types in <OverflowList />');
  }
  _classPrivateFieldLooseBase(this, _refs)[_refs] = {
    defaultSlot,
    overflowSlot,
    moreSlot,
    overflow,
    list,
    placeholder
  };

  // Add event listener for reflow requests
  this.addEventListener('reflow', /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */event => {
    _classPrivateFieldLooseBase(this, _reflowItems)[_reflowItems](0, event.detail.lastVisibleElement);
  });

  // When <overflow-list> is dynamically injected, the browser doesn't remove its <template> automatically.
  // In theory, we could get rid of it now, or in DeclarativeShadowElement, but that would invalidate the layout.
  // Instead, we ignore it for now and remove it later on the first reflow.
  const elements = defaultSlot.assignedElements().filter(element => !(element instanceof HTMLTemplateElement));
  const firstElement = elements[0];
  const lastElement = elements[elements.length - 1];

  // Observe the first and last elements to trigger a reflow when they are visible.
  // That way we can get their height from the IntersectionObserver for free (without reflows).
  if (firstElement) {
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].observe(firstElement);
  }
  if (lastElement && lastElement !== firstElement) {
    _classPrivateFieldLooseBase(this, _intersectionObserver)[_intersectionObserver].observe(lastElement);
  }
}
function _moveItemsToDefaultSlot2() {
  const {
    defaultSlot,
    overflowSlot
  } = _classPrivateFieldLooseBase(this, _refs)[_refs];
  for (const element of overflowSlot.assignedElements()) {
    if (element.slot !== defaultSlot.name) {
      element.slot = defaultSlot.name;
    }
  }
}
function _reset2() {
  const {
    list
  } = _classPrivateFieldLooseBase(this, _refs)[_refs];
  _classPrivateFieldLooseBase(this, _unobserveChanges)[_unobserveChanges]();
  _classPrivateFieldLooseBase(this, _moveItemsToDefaultSlot)[_moveItemsToDefaultSlot]();
  list.style.removeProperty('height');
  this.style.setProperty('--overflow-count', '0');
}
function _updateMinimumReached2(visibleElements) {
  if (this.minimumItems !== null) {
    const minimumReached = visibleElements.length < this.minimumItems;
    if (minimumReached) {
      this.setAttribute('minimum-reached', '');
    } else {
      this.removeAttribute('minimum-reached');
    }
    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }
}
function _observeChanges2() {
  _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].observe(this);
  _classPrivateFieldLooseBase(this, _mutationObserver)[_mutationObserver].observe(this, {
    childList: true
  });
}
function _unobserveChanges2() {
  _classPrivateFieldLooseBase(this, _resizeObserver)[_resizeObserver].disconnect();
  _classPrivateFieldLooseBase(this, _mutationObserver)[_mutationObserver].disconnect();
}
if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}
